import { Copy, Link2, Plus } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toSlug } from "@/lib/toSlug";

import { FeatureGate } from "../entitlements/FeatureGate";
import { StatusBadge } from "../data-display/StatusBadge";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import type { BookingPage } from "./calendarModel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Manage public booking pages. Gated on the Starter-tier calendar_booking
 * entitlement ("Calendar and online booking"); the Starter-vs-Professional
 * split for "Public booking pages" is a recorded pricing ambiguity in
 * CURRENT_STATE.md, revisit when Radi rules on it. Links are shareable, * anonymous visitors book through narrow RPCs, never direct table access.
 */
export const BookingPagesDialog = () => (
  <FeatureGate feature="calendar_booking" fallback={null}>
    <BookingPagesDialogInner />
  </FeatureGate>
);

const BookingPagesDialogInner = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("60");
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending: creating }] = useCreate();
  const [update] = useUpdate();

  const { data: pages } = useGetList<BookingPage>(
    "booking_pages",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "ASC" },
    },
    { enabled: open },
  );

  const bookingUrl = (slug: string) =>
    `${window.location.origin}/#/book/${slug}`;

  const copyLink = async (slug: string) => {
    await navigator.clipboard.writeText(bookingUrl(slug));
    notify("Booking link copied");
  };

  const createPage = () => {
    if (!name.trim()) {
      notify("Give the booking page a name", { type: "warning" });
      return;
    }
    const slug = toSlug(name.trim()).slice(0, 60);
    create(
      "booking_pages",
      {
        data: {
          name: name.trim(),
          slug,
          duration_minutes: Number(duration) || 60,
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Booking page created");
          setName("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not create the booking page",
            { type: "error" },
          ),
      },
    );
  };

  const toggleActive = (page: BookingPage) =>
    update(
      "booking_pages",
      {
        id: page.id,
        data: { active: !page.active },
        previousData: page,
      },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Link2 className="mr-1 size-3.5" />
          Booking pages
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Public booking pages</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-[13px] text-muted-foreground">
            Share a link and let customers book directly into your calendar.
            Slots come from the page's weekly availability minus existing
            appointments.
          </p>
          <div className="flex flex-col gap-2">
            {(pages ?? []).map((page) => (
              <div
                key={page.id}
                className="flex items-center gap-2 rounded-md border border-border-subtle px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium">
                    {page.name}
                    <span className="ml-2 text-[12px] font-normal text-muted-foreground">
                      {page.duration_minutes} min
                    </span>
                  </p>
                  <p className="truncate text-[12px] text-muted-foreground">
                    /#/book/{page.slug}
                  </p>
                </div>
                <StatusBadge
                  label={page.active ? "Active" : "Off"}
                  tone={page.active ? "success" : "neutral"}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[12px]"
                  onClick={() => toggleActive(page)}
                >
                  {page.active ? "Disable" : "Enable"}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7"
                  onClick={() => copyLink(page.slug)}
                >
                  <Copy className="size-3.5" />
                </Button>
              </div>
            ))}
            {(pages ?? []).length === 0 ? (
              <p className="py-2 text-center text-[13px] text-muted-foreground">
                No booking pages yet.
              </p>
            ) : null}
          </div>
          <div className="flex items-end gap-2 border-t border-border-subtle pt-3">
            <label className="flex flex-1 flex-col gap-1 text-[12.5px] font-medium">
              New page name
              <input
                className={inputClass}
                placeholder="Free estimate visit"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="flex w-24 flex-col gap-1 text-[12.5px] font-medium">
              Minutes
              <input
                className={inputClass}
                type="number"
                min="15"
                step="15"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </label>
            <Button disabled={creating} onClick={createPage}>
              <Plus className="mr-1 size-3.5" />
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
